import { ReactNode, useEffect, useRef } from 'react'
import { useRouter } from 'next/router'

import ROUTES from '~/shared/constants/routes.const'
import { Footer } from '~/components/Footer'
import { MenuHorizontal } from '~/components/MenuHorizontal'
import { applyClassNames } from '~/utils/helpers'
import { selectHomebaseIsOpen } from '~/redux/slices/homebase'
import { useAppSelector, useAppDispatch } from '~/redux/hooks'
import { useStoryCards } from '~/gamification/hooks/useStoryCards'
import { selectShowGemShop } from '~/redux/slices/gem-shop'
import ChestAnimation from '~/checklist/components/ChestAnimation'
import { selectChecklistChestAnimation, setChecklistChestAnimation } from '~/redux/slices/dashboard'
import { openUserFeedbackModal } from '~/user-feedback/redux/user-feedback'
import { FeedbackProducts } from '~/user-feedback/constants/userFeedback.const'
import { selectIsNotificationListOpen } from '~/gamification/slices/gamification.slice'
import { Breakpoint, useCheckBreakpoint } from '~/utils/useBreakpoint'
import { selectIsChatAiOpen } from '~/redux/slices/chat-ai'
import { selectIsProgressMigrationFlowOpen, selectIsStateSwitchModalOpen } from '~/redux/slices/modals'
import { PAYWALL_TRIGGER_ATTRIBUTE } from '~/subscription/hooks/useDeferredPaywallTrigger'

import useShowTopbarOnScrolling from '../hooks/useShowTopbarOnScrolling'

import LayoutChatButton from './LayoutChatButton'
import HeaderTop from './components/HeaderTop/HeaderTop'
import MinimalTopbar from './components/HeaderTop/MinimalTopbar'
import usePageBackground from './usePageBackground'

const ROOT_ROUTES_WITH_TOPBAR = [
  ROUTES.HOME,
  ROUTES.ADMIN_PANEL,
  ROUTES.TUTOR_PANEL,
  ROUTES.STUDENT_PANEL,
  ROUTES.GAMES,
  ROUTES.SEARCH_RESULT,
  ROUTES.ENCYCLOPEDIA,
  ROUTES.EXAMS,
  ROUTES.BOOTCAMP,
  ROUTES.DIGITAL_TOOL,
  ROUTES.STUDY_TIPS,
  ROUTES.BOOKING,
  ROUTES.DRILL,
]
const ROOT_ROUTES_WITHOUT_HORIZONTAL_MENU = [
  ROUTES.SUBSCRIPTION_PAGE,
  ROUTES.ONBOARDING_ACCOUNT_TYPE,
  ROUTES.ONBOARDING_UPDATE_EMAIL,
  ROUTES.ONBOARDING_DETAILS,
  ROUTES.ONBOARDING_UPDATE_EMAIL,
  ROUTES.AVATAR,
  ROUTES.AVATAR_MULT_GAME,
  ROUTES.BOOTCAMP_ACTIVITY,
]

const shouldShowTopbar = (pathname: string): boolean =>
  Boolean(ROOT_ROUTES_WITH_TOPBAR.find((routePath) => pathname.indexOf(routePath) !== -1))

const shouldHideHorizontalMenu = (pathname: string): boolean => {
  return Boolean(ROOT_ROUTES_WITHOUT_HORIZONTAL_MENU.find((routePath) => pathname.indexOf(routePath) === 0))
}

export function DefaultLayout({
  children,
  zeroPadding,
  topbarClassName = '',
  hideTopbar = false,
  minimalTopbar = false,
  hideMobileBottomMenu = false,
  hideAIButton = false,
  hideFooter = false,
  dashboardGradientClass = '',
  disableScroll = false,
}: {
  children: ReactNode
  zeroPadding?: boolean
  topbarClassName?: string
  hideTopbar?: boolean
  minimalTopbar?: boolean
  hideMobileBottomMenu?: boolean
  hideAIButton?: boolean
  hideFooter?: boolean
  dashboardGradientClass?: string
  disableScroll?: boolean
}): React.ReactElement {
  useStoryCards()

  const router = useRouter()

  const { cx, style } = usePageBackground()
  const bgGradientPurpleToBlue = 'bg-gradient-to-r from-purple to-[#58C1FC]'
  const showTopbar = shouldShowTopbar(router.pathname)
  const hideHorizontalMenu = hideMobileBottomMenu || shouldHideHorizontalMenu(router.pathname)
  const homebaseIsOpen = useAppSelector(selectHomebaseIsOpen)
  const isGemShopOpen = useAppSelector(selectShowGemShop)
  const chestAnimation = useAppSelector(selectChecklistChestAnimation)
  const isScreenSM = useCheckBreakpoint({ lt: Breakpoint.SM })
  const isNotificationOpen = useAppSelector(selectIsNotificationListOpen)
  const oracleIsOpen = useAppSelector(selectIsChatAiOpen)
  const isProgressMigrationFlowOpen = useAppSelector(selectIsProgressMigrationFlowOpen)
  const isStateSwitchModalOpen = useAppSelector(selectIsStateSwitchModalOpen)
  const isNotificationOpenMobile = isScreenSM && isNotificationOpen
  const dispatch = useAppDispatch()

  const layoutContainerClassNames = {
    background: getBackground(router.asPath),
    size: 'h-max min-h-screen',
  }

  const disableScrollPageContent =
    homebaseIsOpen ||
    isGemShopOpen ||
    (oracleIsOpen && isScreenSM) ||
    isNotificationOpenMobile ||
    isProgressMigrationFlowOpen ||
    isStateSwitchModalOpen ||
    disableScroll

  useEffect(() => {
    if (disableScrollPageContent) document.body.style.overflow = 'hidden'
    else document.body.style.overflow = 'unset'

    // Each page mounts its own layout, so leaving one mid-lock (e.g. Back to a ClearLayout page) must release it.
    return () => {
      document.body.style.overflow = ''
    }
  }, [disableScrollPageContent])

  const headerTopRef = useRef<HTMLDivElement>(null)

  useShowTopbarOnScrolling(headerTopRef)

  const innerClassNames = {
    box: 'box-content flex flex-col lg:flex-row',
    transition: 'transition duration-300',
  }
  const bookingClassName = {
    box: 'box-content flex flex-col lg:flex-row',
    background: `bg-no-repeat bg-far-left-bottom`,
  }

  /* When zeroPadding is true, no padding will be applied around children.
  Helpful for pages where full size background images are used */
  const childrenPadding = zeroPadding ? '' : 'py-8 px-4 lg:px-8'

  const isBooking = router.asPath === ROUTES.BOOKING
  const isCheckout = router.asPath.startsWith(ROUTES.BOOKING_CHECKOUT)

  const isTopbarVisible = hideTopbar || minimalTopbar ? false : !!showTopbar && !isProgressMigrationFlowOpen

  const handleChestAnimationEnd = (): void => {
    dispatch(setChecklistChestAnimation(false))
    dispatch(openUserFeedbackModal({ product: FeedbackProducts.checklist }))
  }

  const chestAnimationClass = chestAnimation ? 'block' : 'hidden'

  return (
    <>
      <div className={applyClassNames(layoutContainerClassNames)} data-testid="default-layout">
        <div
          className={`${applyClassNames(innerClassNames)} ${
            isBooking || isCheckout ? bgGradientPurpleToBlue : ''
          } ${dashboardGradientClass}`}
        >
          <div className={`${chestAnimationClass}`}>
            <ChestAnimation chestAnimation={chestAnimation} handleAnimation={handleChestAnimationEnd} />
          </div>
          <div
            className={`lg:w-full lg:min-h-screen flex flex-col ${cx} ${
              isBooking ? `relative -top-3 -mb-3 lg:top-0 lg:mb-0 overflow-x-hidden` : ''
            }`}
            style={style}
          >
            {isBooking ? (
              <div className={`absolute w-full h-full -ml-4 md:-ml-16 lg:-ml-4 ${bookingClassName.background}`} />
            ) : (
              ''
            )}
            {isTopbarVisible ? (
              <div className="top-0 z-modal-top sticky w-full duration-500 ease-in-out" ref={headerTopRef}>
                <HeaderTop
                  className={`${topbarClassName} w-full ${isBooking ? 'pt-3 lg:pt-0' : ''}`}
                  hideAIButton={hideAIButton}
                />
              </div>
            ) : minimalTopbar ? (
              <div className="top-0 z-modal-top sticky w-full duration-500 ease-in-out" ref={headerTopRef}>
                <MinimalTopbar />
              </div>
            ) : (
              ''
            )}
            <div
              className={`flex flex-col w-full min-h-screen-off-16 ${
                isTopbarVisible || minimalTopbar ? 'duration-500' : ''
              } ${isCheckout ? '' : childrenPadding} ${isBooking ? 'z-10' : ''}`}
              {...{ [PAYWALL_TRIGGER_ATTRIBUTE]: 'true' }}
            >
              {children}
            </div>
          </div>
          {!hideHorizontalMenu ? <MenuHorizontal /> : ''}
        </div>
      </div>
      <LayoutChatButton hideBottomMenu={hideHorizontalMenu} hideChatButton={hideAIButton} />
      {!hideFooter && <Footer hideHorizontalMenu={hideHorizontalMenu} />}
    </>
  )
}

function getBackground(path: string): string {
  const route = `/${path.split('/').filter((part) => !part.includes('campaign'))[1]}`

  if (path.startsWith(`${ROUTES.BOOKING}/`)) {
    return 'bg-gray-off-white'
  }

  if (path.startsWith(`${ROUTES.TUTOR_ON_DEMAND}`)) {
    return 'bg-gray-off-white'
  }

  if (path.startsWith(`${ROUTES.CAMPAIGNS}${ROUTES.TUTOR_ON_DEMAND}`)) {
    return 'bg-gray-off-white'
  }

  if (path.startsWith(ROUTES.MEDIA)) {
    return 'bg-gray-off-white'
  }

  if (route === ROUTES.ADMIN_PANEL || route === ROUTES.TUTOR_PANEL || route === ROUTES.STUDENT_PANEL) {
    return 'bg-gray-off-white'
  }

  if (path.startsWith(ROUTES.ONBOARDING)) {
    return 'bg-gray-off-white'
  }

  if (path.startsWith(ROUTES.DEV)) {
    return 'bg-gray-off-white'
  }

  return 'bg-gradient-purple-dark'
}
